import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_GROUND,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_DYNAMIC_PROP,
  COLLISION_GROUP_AI_VEHICLE,
  COLLISION_GROUP_PEDESTRIAN,
} from '../core/PhysicsWorld';
import { Powertrain } from './Powertrain';

export interface CollisionEvent {
  impactSpeedKmh: number;
  impactIntensity: number; // 0.0 to 1.0
  contactNormal: CANNON.Vec3;
  contactPoint: CANNON.Vec3;
  targetBody: CANNON.Body | null;
  timestamp: number;
  playerSpeedKmh: number;
  isNpcFault?: boolean;
}

export interface VehicleTelemetry {
  position: CANNON.Vec3;
  quaternion: CANNON.Quaternion;
  velocity: CANNON.Vec3;
  speedKmh: number;
  steeringAngle: number;
  throttle: number;
  brake: number;
  handbrake: boolean;
  rpm: number;
  gear: string;
  isStalled: boolean;
  engineRunning: boolean;
  seatbeltFastened: boolean;
  clutch: number;
  lateralG: number;
  isReversing: boolean;
  distanceTraveledMeters: number;
}

export class VehiclePhysics {
  public chassisBody: CANNON.Body;
  public vehicle: CANNON.RaycastVehicle;
  public powertrain: Powertrain;

  public currentSteeringAngle = 0;
  public maxSteeringAngle = 0.62; // ~35.5 degrees — VW Golf front wheel lock

  public speedKmh = 0;
  public distanceTraveledMeters = 0;
  private lastPosition = new CANNON.Vec3();

  // Smoothed drive force (prevents instant torque lurch)
  public smoothedDriveForce = 0;  // public for debug overlay access

  // Collision callback system
  private onCollisionCallbacks: Array<(event: CollisionEvent) => void> = [];
  private lastCollisionTime: number = 0;

  public onCollision(cb: (event: CollisionEvent) => void) {
    this.onCollisionCallbacks.push(cb);
  }

  constructor(physicsWorld: PhysicsWorld, startPosition: CANNON.Vec3 = new CANNON.Vec3(-2.0, 0.5, 24), startRotationY: number = 0) {
    this.powertrain = new Powertrain('automatic');

    // 1. Chassis — realistic mass, minimal artificial damping
    //    Let tire friction and drag handle deceleration naturally
    const chassisShape = new CANNON.Box(new CANNON.Vec3(0.9, 0.45, 2.1));
    const lowerBumperShape = new CANNON.Box(new CANNON.Vec3(0.88, 0.18, 2.18));
    this.chassisBody = new CANNON.Body({
      mass: 1350,          // kg — mid-size hatchback (VW Golf)
      material: physicsWorld.chassisMaterial,
      linearDamping: 0.05, // Aerodynamic drag only — NOT a driving force
      angularDamping: 0.4, // Light yaw damping — chassis still rotates naturally
      collisionFilterGroup: COLLISION_GROUP_CHASSIS,
      collisionFilterMask:
        COLLISION_GROUP_GROUND |
        COLLISION_GROUP_STATIC |
        COLLISION_GROUP_DYNAMIC_PROP |
        COLLISION_GROUP_AI_VEHICLE |
        COLLISION_GROUP_PEDESTRIAN,
    });
    this.chassisBody.addShape(chassisShape, new CANNON.Vec3(0, 0.45, 0));
    this.chassisBody.addShape(lowerBumperShape, new CANNON.Vec3(0, 0.02, 0));
    this.chassisBody.position.copy(startPosition);
    this.chassisBody.quaternion.setFromEuler(0, startRotationY, 0);
    this.chassisBody.updateAABB();
    this.chassisBody.allowSleep = false;
    this.lastPosition.copy(startPosition);

    // Collision listener on chassis body
    this.chassisBody.addEventListener('collide', (e: any) => {
      const now = performance.now();
      // Debounce rapid collision events (at least 60ms between impact registrations)
      if (now - this.lastCollisionTime < 60) return;

      const contact = e.contact;
      const otherBody: CANNON.Body = e.body;

      // Ignore regular ground plane resting contacts
      const isGroundPlane = otherBody && (
        otherBody.collisionFilterGroup === COLLISION_GROUP_GROUND ||
        (otherBody.shapes && otherBody.shapes[0] instanceof CANNON.Plane)
      );
      if (isGroundPlane) {
        // Only register severe bottom-out scrapes
        const vertVel = Math.abs(this.chassisBody.velocity.y);
        if (vertVel < 2.0) return;
      }

      const relVel = Math.abs(contact ? contact.getImpactVelocityAlongNormal() : 0);
      const isPedestrian = otherBody && otherBody.collisionFilterGroup === COLLISION_GROUP_PEDESTRIAN;
      const impactSpeedKmh = Math.max(relVel * 3.6, Math.abs(this.speedKmh));
      const playerSpeedKmh = Math.round(Math.abs(this.speedKmh) * 10) / 10;

      // Register collision if speed exceeds 1.2 km/h (catching low-speed parking touches) OR if impacting a pedestrian
      if (impactSpeedKmh > 1.2 || isPedestrian) {
        this.lastCollisionTime = now;
        const intensity = Math.min(1.0, Math.max(0.12, impactSpeedKmh / 55.0));

        // Determine fault attribution:
        let isNpcFault = false;
        // 1. Stationary / start standstill defense (speed < 3.0 km/h)
        if (playerSpeedKmh < 3.0 && otherBody) {
          if (isPedestrian) {
            // Reversing or driving into a pedestrian is always driver fault
            const isReversing = this.powertrain.autoGear === 'R' || this.powertrain.manualGear === -1;
            if (playerSpeedKmh < 0.3 && !isReversing) {
              isNpcFault = true;
            }
          } else {
            isNpcFault = true;
          }
        }

        // 2. In-lane rear-end impact detection (struck from behind by faster following traffic)
        if (!isNpcFault && otherBody && otherBody.collisionFilterGroup === COLLISION_GROUP_AI_VEHICLE) {
          const forward = new CANNON.Vec3(0, 0, 1);
          this.chassisBody.quaternion.vmult(forward, forward);
          const contactPt = contact && contact.rj
            ? contact.rj.clone().vadd(this.chassisBody.position)
            : this.chassisBody.position.clone();
          const toContact = contactPt.vsub(this.chassisBody.position);
          if (toContact.dot(forward) < -0.4 && this.speedKmh > 0) {
            isNpcFault = true;
          }
        }

        const event: CollisionEvent = {
          impactSpeedKmh: Math.round(Math.max(impactSpeedKmh, isPedestrian ? 1.0 : 0) * 10) / 10,
          impactIntensity: intensity,
          contactNormal: contact && contact.ni ? contact.ni.clone() : new CANNON.Vec3(0, 1, 0),
          contactPoint: contact && contact.rj
            ? contact.rj.clone().vadd(this.chassisBody.position)
            : this.chassisBody.position.clone(),
          targetBody: otherBody || null,
          timestamp: now,
          playerSpeedKmh,
          isNpcFault,
        };

        for (const cb of this.onCollisionCallbacks) {
          cb(event);
        }
      }
    });


    // 2. Raycast Vehicle
    this.vehicle = new CANNON.RaycastVehicle({
      chassisBody: this.chassisBody,
      indexRightAxis: 0,
      indexUpAxis: 1,
      indexForwardAxis: 2,
    });

    // Wheel setup — realistic suspension and grip for a German exam car
    const wheelOptions = {
      radius: 0.32,
      directionLocal: new CANNON.Vec3(0, -1, 0),
      suspensionStiffness: 32,        // Firm German road suspension
      suspensionRestLength: 0.40,
      frictionSlip: 3.6,              // Calibrated realistic lateral grip
      dampingRelaxation: 2.8,         // Rebound damping
      dampingCompression: 4.2,        // Compression damping
      maxSuspensionForce: 80000,
      rollInfluence: 0.12,            // Slight body roll — realistic for exam car
      axleLocal: new CANNON.Vec3(1, 0, 0),
      chassisConnectionPointLocal: new CANNON.Vec3(1, 0, 1),
      maxSuspensionTravel: 0.40,
      customSlidingRotationalSpeed: -30,
      useCustomSlidingRotationalSpeed: true,
    };


    const halfTrack = 0.78;
    const frontAxleZ = 1.31;   // Golf wheelbase/2 = 2.62/2 = 1.31
    const rearAxleZ = -1.31;
    const suspensionY = 0.25;

    // FL(0), FR(1), RL(2), RR(3)
    wheelOptions.chassisConnectionPointLocal.set( halfTrack, suspensionY, frontAxleZ);
    this.vehicle.addWheel(wheelOptions);
    wheelOptions.chassisConnectionPointLocal.set(-halfTrack, suspensionY, frontAxleZ);
    this.vehicle.addWheel(wheelOptions);
    wheelOptions.chassisConnectionPointLocal.set( halfTrack, suspensionY, rearAxleZ);
    this.vehicle.addWheel(wheelOptions);
    wheelOptions.chassisConnectionPointLocal.set(-halfTrack, suspensionY, rearAxleZ);
    this.vehicle.addWheel(wheelOptions);

    this.vehicle.addToWorld(physicsWorld.world);
  }

  public isHandbrakeActive = false;
  private static readonly FORWARD_AXIS = new CANNON.Vec3(0, 0, 1);
  private forwardVectorScratch = new CANNON.Vec3();

  public update(deltaTime: number, throttle: number, brake: number, steeringInput: number, handbrake: boolean, clutch: number) {
    this.isHandbrakeActive = handbrake;

    // ─── 1. Speed & Road Angle Orientation ────────────────────────────────────
    const velocity = this.chassisBody.velocity;
    this.chassisBody.quaternion.vmult(VehiclePhysics.FORWARD_AXIS, this.forwardVectorScratch);
    let forwardSpeed = velocity.dot(this.forwardVectorScratch);
    const absSpeed = Math.abs(forwardSpeed);

    // Road inclination angle:
    // forwardVectorScratch.y is sin(pitch). 0.0 on flat road (0 degrees).
    // Positive = facing uphill, negative = facing downhill.
    const roadPitchSin = this.forwardVectorScratch.y;
    const isFlatRoad = Math.abs(roadPitchSin) < 0.02; // < ~1.1 degrees slope

    // ─── 2. Zero-Drift Stationary Hold ────────────────────────────────────────
    // When the vehicle is stopped or coming to a stop (< 0.5 km/h) AND:
    //  a) Foot brake is pressed (brake > 0.05), OR
    //  b) Handbrake is engaged (handbrake === true), OR
    //  c) Transmission is in Park (P), OR
    //  d) Engine off / stalled without throttle, OR
    //  e) Flat road at complete rest with no throttle and no creep
    //
    // Clamping velocity.x and velocity.z to 0 simulates true static friction (Haftreibung),
    // completely preventing the car from bouncing on suspension springs or drifting forward/backward.
    const isBrakingHold = (brake > 0.05 || handbrake || this.powertrain.autoGear === 'P') && throttle < 0.05;
    // isAutoCreeping is only true when the car is ACTUALLY rolling fast enough for creep torque (>2 km/h).
    // At standstill, creep must never prevent the zero-drift hold from engaging.
    const isAutoCreeping = this.powertrain.type === 'automatic' &&
      (this.powertrain.autoGear === 'D' || this.powertrain.autoGear === 'R') &&
      this.powertrain.engineRunning && !this.powertrain.isStalled && brake < 0.04 &&
      absSpeed > 0.56; // 0.56 m/s ≈ 2.0 km/h — matches Powertrain creep threshold
    const isRestHold = !this.powertrain.engineRunning || this.powertrain.isStalled ||
      (isFlatRoad && throttle < 0.05 && brake < 0.05 && !isAutoCreeping && absSpeed < 0.08);

    if ((isBrakingHold || isRestHold) && absSpeed < 0.08) {
      forwardSpeed = 0;
      this.chassisBody.velocity.x = 0;
      this.chassisBody.velocity.z = 0;
      this.chassisBody.angularVelocity.set(0, 0, 0);
    }

    this.speedKmh = forwardSpeed * 3.6;

    const displacement = this.chassisBody.position.distanceTo(this.lastPosition);
    if (displacement > 0.001 && displacement < 10 && Math.abs(this.speedKmh) > 0.1) {
      this.distanceTraveledMeters += displacement;
    }
    this.lastPosition.copy(this.chassisBody.position);

    // ─── 3. Steering — speed-sensitive with Ackermann geometry ───────────────
    // Dynamic speed-sensitive maximum lock (35.5° parking, ~18° city, ~11° highway)
    const absSpeedKmh = Math.abs(this.speedKmh);
    const speedLockFactor = 1.0 / (1.0 + (absSpeedKmh / 28) * 0.95);
    const maxAngleAtSpeed = this.maxSteeringAngle * Math.max(0.22, speedLockFactor);
    const targetSteering = steeringInput * maxAngleAtSpeed;

    // Responsive yet smoothly damped rack rates
    const physicsSteerApplying = Math.abs(steeringInput) > 0.01;
    const steerRate = physicsSteerApplying
      ? (absSpeedKmh < 35 ? 4.5 : Math.max(3.6, 4.5 - (absSpeedKmh - 35) * 0.02)) // gentle, non-twitchy active steering
      : 5.8; // smoothly damped self-centring return
    this.currentSteeringAngle += (targetSteering - this.currentSteeringAngle) * Math.min(1.0, deltaTime * steerRate);

    // Caster self-centring — at speed > 10 km/h without input, pneumatic trail aligns wheels straight
    if (!physicsSteerApplying && absSpeedKmh > 10) {
      const casterStrength = Math.min(0.08, (absSpeedKmh - 10) / 450);
      this.currentSteeringAngle *= (1.0 - casterStrength);
      if (Math.abs(this.currentSteeringAngle) < 0.002) {
        this.currentSteeringAngle = 0;
      }
    }

    // Ackermann correction
    // NOTE: In Cannon-ES with vehicle moving forward (+Z), positive steering rotates towards +X (Screen Left),
    // and negative steering rotates towards -X (Screen Right).
    // Therefore, a left turn (currentSteeringAngle < 0) corresponds to positive Cannon steering values,
    // and a right turn (currentSteeringAngle > 0) corresponds to negative Cannon steering values.
    const wheelbase = 2.62;
    const track = 1.56;
    let steerFL = -this.currentSteeringAngle;
    let steerFR = -this.currentSteeringAngle;
    if (Math.abs(this.currentSteeringAngle) > 0.01) {
      const R = wheelbase / Math.tan(Math.abs(this.currentSteeringAngle));
      const innerAngle = Math.atan(wheelbase / (R - track / 2));
      const outerAngle = Math.atan(wheelbase / (R + track / 2));
      if (this.currentSteeringAngle > 0) {
        // Right turn: Wheel 1 (FR) is inner (-innerAngle), Wheel 0 (FL) is outer (-outerAngle)
        steerFR = -innerAngle;
        steerFL = -outerAngle;
      } else {
        // Left turn: Wheel 0 (FL) is inner (+innerAngle), Wheel 1 (FR) is outer (+outerAngle)
        steerFL = innerAngle;
        steerFR = outerAngle;
      }
    }
    this.vehicle.setSteeringValue(steerFL, 0);
    this.vehicle.setSteeringValue(steerFR, 1);

    // ─── 4. Powertrain ────────────────────────────────────────────────────────
    this.powertrain.clutchPedal = clutch;
    const ptResult = this.powertrain.update(deltaTime, throttle, brake, this.speedKmh);

    // ─── 5. Drive Force with Inertia Ramp-Up ─────────────────────────────────
    // German 1.5L TSI hatchback: ~7,000 N peak in 1st gear (~5.2 m/s² = 0.53 G).
    // Acceleration feels authentic like in a real car.
    const driveMultiplier = 2.6;
    const targetDriveForce = ptResult.isReverse
      ? -ptResult.engineTorque * driveMultiplier
      :  ptResult.engineTorque * driveMultiplier;

    // Brake Override System (BOS) & Auto-Hold:
    // When the brake pedal is pressed (brake > 0.05) or Auto-Hold is active, cut drive torque immediately!
    if (brake > 0.05 || this.autoHold) {
      this.smoothedDriveForce = 0;
    } else {
      const torqueRampRate = throttle > 0.05 ? 7.0 : 5.0;
      this.smoothedDriveForce += (targetDriveForce - this.smoothedDriveForce) * Math.min(1.0, deltaTime * torqueRampRate);
    }

    // Distribute to driven wheels (FWD — front wheels only)
    // SIGN: Cannon-ES updateFriction computes forwardWS = surfNormal × axle = (0,0,-1).
    // Negate so positive smoothedDriveForce → impulse in +Z = FORWARD.
    const forcePerWheel = -(this.smoothedDriveForce / 2.0);
    this.vehicle.applyEngineForce(forcePerWheel, 0); // FL
    this.vehicle.applyEngineForce(forcePerWheel, 1); // FR
    this.vehicle.applyEngineForce(0, 2);             // RL
    this.vehicle.applyEngineForce(0, 3);             // RR

    // ─── 6. Braking — realistic ABS-assisted speed-proportional deceleration ──
    // Physics & Mechanical calibration:
    // In Cannon-ES, `wheel.brake` is an IMPULSE cap per timestep (in N·s), NOT continuous force in Newtons.
    // Continuous force in Newtons is converted to impulse per timestep:
    //   Impulse = Force × deltaTime
    //
    // Realistic Deceleration Scale:
    //   - Normal comfort braking (brake ≈ 0.3 - 0.5): ~2.5 to 4.5 m/s²
    //   - Firm driving-school stop (brake ≈ 0.6 - 0.8): ~5.0 to 7.0 m/s²
    //   - German StVO § 4 / § 34 emergency braking (Gefahrbremsung, brake = 1.0): ~8.5 to 9.0 m/s² (~0.9G peak ABS)

    // Progressive hydraulic pedal pressure ramp (~90ms build-up lag)
    // Prevents abrupt binary shock while keeping responsiveness high
    const brakeRampRate = brake > 0.02
      ? (brake > 0.75 ? 8.5 : 6.0)
      : 14.0; // quick pressure release when lifting foot
    if (this._smoothedBrakePressure === undefined) this._smoothedBrakePressure = 0;
    this._smoothedBrakePressure += (brake - this._smoothedBrakePressure) * Math.min(1.0, deltaTime * brakeRampRate);
    const smoothBrake = this._smoothedBrakePressure;

    const vehicleMass = 1350; // kg
    // Non-linear progressive brake response curve:
    // f(b) = b^1.25 gives gentle, controllable initial bite (~2.2-3.2 m/s² service braking)
    // that builds progressively to full ABS stopping (~8.0-8.5 m/s² StVO § 34 Gefahrbremsung)
    const progressiveBrakeFactor = Math.pow(Math.max(0, Math.min(1.0, smoothBrake)), 1.25);
    const maxDeceleration = 8.5; // m/s² peak ABS deceleration on dry German tarmac
    const targetBrakeForce = progressiveBrakeFactor * vehicleMass * maxDeceleration;

    let totalBrakeImpulse = targetBrakeForce * deltaTime;

    // Multi-wheel anti-overshoot clamping at low speeds:
    // When approaching 0 km/h (< 1.2 m/s), cap the total stopping impulse so it never
    // exceeds the vehicle's remaining linear momentum (M × |v|).
    // This prevents the wheels from reversing the velocity vector and causing wild back-and-forth oscillations.
    if (absSpeed < 1.2) {
      const remainingMomentum = absSpeed * vehicleMass;
      totalBrakeImpulse = Math.min(totalBrakeImpulse, remainingMomentum);
    }

    // Road Angle / Slope Physics Compensation:
    // On an inclined road, gravity exerts a longitudinal force downhill: F = M * g * sin(pitch).
    // Calculate the static holding impulse needed per wheel to counteract gravity on hills:
    const hillHoldImpulsePerWheel = Math.abs(vehicleMass * 9.81 * roadPitchSin * deltaTime) / 4.0;

    // Electronic Brake-force Distribution (EBD):
    // 65% front / 35% rear at moderate speeds; slightly higher front bias at high speed
    const frontBias = absSpeed > 8.0 ? 0.68 : 0.64;
    let frontWheelBrake = (totalBrakeImpulse * frontBias) / 2.0;
    let rearWheelBrake  = (totalBrakeImpulse * (1.0 - frontBias)) / 2.0;

    // If braking, holding on a hill, or in Park, add the hill hold budget
    if (brake > 0.05 || handbrake || this.powertrain.autoGear === 'P') {
      frontWheelBrake += hillHoldImpulsePerWheel;
      rearWheelBrake  += hillHoldImpulsePerWheel;
    }

    // Engine braking (compression braking when off throttle in gear)
    if (throttle < 0.05 && absSpeed > 1.5 && ptResult.engineTorque < 0) {
      const engBrakeImpulse = (Math.abs(ptResult.engineTorque) * 0.35 * deltaTime) / 2.0;
      frontWheelBrake += engBrakeImpulse;
    }

    // Transmission Park gear ('P') lock or stalled resistance
    if (this.powertrain.autoGear === 'P' || this.powertrain.isStalled) {
      const parkImpulse = (vehicleMass * 9.81 * deltaTime) / 4.0;
      frontWheelBrake = Math.max(frontWheelBrake, parkImpulse);
      rearWheelBrake  = Math.max(rearWheelBrake,  parkImpulse);
    }

    // Handbrake — locks rear wheels only with full parking cable tension
    if (handbrake) {
      const handbrakeImpulse = (vehicleMass * 9.81 * deltaTime) / 2.0;
      rearWheelBrake = Math.max(rearWheelBrake, handbrakeImpulse);
    }

    // ─── 7. Auto-Hold System (German Standstill Assist / Berganfahrassistent) ──
    // When the vehicle comes to a standstill, Auto-Hold engages automatically.
    // Releasing the brake pedal will NEVER let the car roll or creep away uncommanded!
    // Auto-Hold only disengages when the driver intentionally presses the GAS pedal (throttle > 0.05).
    if (this.autoHold) {
      if (throttle > 0.05) {
        this.autoHold = false;
      } else {
        const holdImpulse = (vehicleMass * 9.81 * deltaTime) / 2.0;
        frontWheelBrake = Math.max(frontWheelBrake, holdImpulse);
        rearWheelBrake  = Math.max(rearWheelBrake, holdImpulse);
        this.chassisBody.velocity.set(0, 0, 0);
        this.chassisBody.angularVelocity.set(0, 0, 0);
      }
    } else if (absSpeed < 0.08 && (brake > 0.05 || (throttle < 0.02 && absSpeed < 0.05))) {
      // Engage Auto-Hold when car has decelerated to true standstill without throttle.
      this.autoHold = true;
    }

    this.vehicle.setBrake(frontWheelBrake, 0); // FL
    this.vehicle.setBrake(frontWheelBrake, 1); // FR
    this.vehicle.setBrake(rearWheelBrake,  2); // RL
    this.vehicle.setBrake(rearWheelBrake,  3); // RR
  }

  private _smoothedBrakePressure: number = 0;
  public get smoothedBrakePressure(): number {
    return this._smoothedBrakePressure || 0;
  }
  public autoHold: boolean = true;

  public seatbeltFastened: boolean = false;

  public getTelemetry(throttle: number, brake: number, handbrake: boolean, clutch: number): VehicleTelemetry {
    const ptState = this.powertrain.getState(this.speedKmh);
    const gearLabel = ptState.transmissionType === 'automatic'
      ? `${ptState.autoGear}${ptState.autoGear === 'D' ? ptState.manualGear : ''}`
      : ptState.manualGear === -1 ? 'R' : ptState.manualGear === 0 ? 'N' : `${ptState.manualGear}`;

    return {
      position: this.chassisBody.position.clone(),
      quaternion: this.chassisBody.quaternion.clone(),
      velocity: this.chassisBody.velocity.clone(),
      speedKmh: Math.abs(this.speedKmh) < 0.3 ? 0 : Math.round(this.speedKmh * 10) / 10,
      steeringAngle: this.currentSteeringAngle,
      throttle,
      brake,
      handbrake,
      rpm: Math.round(this.powertrain.rpm),
      gear: gearLabel,
      isStalled: this.powertrain.isStalled,
      engineRunning: this.powertrain.engineRunning,
      seatbeltFastened: this.seatbeltFastened,
      clutch,
      lateralG: 0,
      isReversing: ptState.autoGear === 'R' || ptState.manualGear === -1,
      distanceTraveledMeters: this.distanceTraveledMeters,
    };
  }

  public resetPosition(pos: CANNON.Vec3, rotY: number = 0) {
    this.chassisBody.position.copy(pos);
    this.chassisBody.quaternion.setFromEuler(0, rotY, 0);
    this.chassisBody.updateAABB();
    this.chassisBody.velocity.set(0, 0, 0);
    this.chassisBody.angularVelocity.set(0, 0, 0);
    this.chassisBody.allowSleep = false; // keep awake so engine forces are always processed
    this.chassisBody.wakeUp();           // ensure any prior sleep state is cleared
    this.currentSteeringAngle = 0;
    this.smoothedDriveForce = 0;
    this.speedKmh = 0;
    this.distanceTraveledMeters = 0;
    this.lastPosition.copy(pos);
    this._smoothedBrakePressure = 0;
    this.powertrain.stopEngine(); // Engine starts OFF for authentic in-cockpit pre-drive check
    this.seatbeltFastened = false; // Seatbelt starts unfastened
    this.autoHold = true;
  }
}
